/**
 * The "today" parts of the dashboard (ADR 0028): a capture field, "Mijn dag" as a
 * timeline of today's tasks and reminders, and today's progress as a ring.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Pin, Plus } from 'lucide-react';
import { expandCapture, isOpen } from '@nexus/core';
import {
  addTaskToPage,
  createMemo,
  inboxPage,
  memoFromCapture,
  toggleDone,
  type Memo,
  type Task,
} from '@nexus/workspace';
import { useStore } from '../../db/context';
import { Dialog } from '../Dialog';
import { TaskRow } from '../tasks/TaskRow';
import { useTasks } from '../../db/tasks';
import { useMemos } from '../../memos/data';
import { useUi } from '../../state/ui';
import { cn } from '../../lib/cn';
import { displayTitle, todayIso } from '../tasks/format';

/** "Wat wil je doen?": a task for the Inbox, or (pin) something to remember. */
export function DashboardCapture() {
  const { t } = useTranslation();
  const store = useStore();
  const [text, setText] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const add = async (asMemo: boolean) => {
    const value = text.trim();
    if (!value) return;
    if (asMemo) await createMemo(store, memoFromCapture(value, todayIso()));
    else await addTaskToPage(store, (await inboxPage(store)).id, expandCapture(value, todayIso()));
    setText('');
    setDone(t(asMemo ? 'dashboard.capture.savedMemo' : 'dashboard.capture.savedTask'));
    setTimeout(() => setDone(null), 2500);
  };
  return (
    <form
      className="mb-6"
      onSubmit={(e) => {
        e.preventDefault();
        void add(false);
      }}
    >
      <div className="flex items-center gap-2 rounded-full border border-border bg-bg py-1 pr-1 pl-4 shadow-sheet focus-within:border-accent">
        <Plus className="size-4 shrink-0 text-fg-muted" />
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t('dashboard.capture.placeholder')}
          aria-label={t('dashboard.capture.label')}
          className="h-9 min-w-0 flex-1 bg-transparent text-sm outline-none"
        />
        <button
          type="button"
          onClick={() => void add(true)}
          disabled={!text.trim()}
          title={t('dashboard.capture.asMemo')}
          aria-label={t('dashboard.capture.asMemo')}
          className="mod-memos mod-icon size-9 disabled:opacity-40"
        >
          <Pin className="size-4" />
        </button>
        <button
          type="submit"
          disabled={!text.trim()}
          className="h-9 rounded-full bg-accent px-4 text-sm font-medium text-accent-fg disabled:opacity-40"
        >
          {t('dashboard.capture.asTask')}
        </button>
      </div>
      <p className="mt-1.5 min-h-4 pl-4 text-xs text-fg-muted" aria-live="polite">
        {done ?? t('dashboard.capture.hint')}
      </p>
    </form>
  );
}

type Item =
  | { kind: 'task'; id: string; time: string | null; task: Task }
  | { kind: 'memo'; id: string; time: string | null; memo: Memo };

function dayItems(tasks: Task[], memos: Memo[], today: string) {
  const open = tasks.filter((x) => isOpen(x.status));
  const overdue = open.filter((x) => x.due && x.due < today);
  const items: Item[] = [
    ...tasks
      .filter(
        (x) =>
          // Open ones for today, and what was done today (shown struck through).
          (isOpen(x.status) &&
            (x.due === today || (!x.due && x.scheduled && x.scheduled <= today))) ||
          (x.status === 'done' && x.doneAt === today && x.due === today),
      )
      .map((task) => ({ kind: 'task' as const, id: task.id, time: task.dueTime, task })),
    ...memos
      .filter((m) => m.remindAt?.slice(0, 10) === today)
      .map((memo) => ({
        kind: 'memo' as const,
        id: memo.id,
        time: memo.remindAt!.slice(11, 16),
        memo,
      })),
  ];
  // Timed items in order of the clock, then the rest.
  items.sort((a, b) =>
    a.time && b.time ? a.time.localeCompare(b.time) : a.time ? -1 : b.time ? 1 : 0,
  );
  return { overdue, items };
}

const MAX = 9;

/** "Mijn dag": today's tasks and reminders on a timeline, overdue ones above. */
export function DayBlock() {
  const { t } = useTranslation();
  const store = useStore();
  const tasks = useTasks();
  const memos = useMemos();
  const open = useUi((s) => s.open);
  if (!tasks || !memos) return null;
  const { overdue, items } = dayItems(tasks, memos, todayIso());

  const row = (item: Item) => (
    <li key={item.id} className="group relative flex items-center gap-3 py-1.5 pl-5">
      <span
        aria-hidden
        className={cn(
          'absolute top-1/2 left-[3px] size-2 -translate-y-1/2 rounded-full',
          item.kind === 'memo' ? 'bg-[var(--mod-memos)]' : 'bg-accent-bright',
        )}
      />
      <span className="w-11 shrink-0 text-xs text-fg-muted tabular-nums">{item.time ?? '—'}</span>
      {item.kind === 'task' ? (
        <input
          type="checkbox"
          checked={item.task.status === 'done'}
          aria-label={t('tasks.toggle', { title: item.task.title })}
          onChange={() => void toggleDone(store, item.task)}
          className="nx-check shrink-0"
        />
      ) : (
        <Pin aria-hidden className="mod-memos mod-ink size-4 shrink-0" />
      )}
      <button
        type="button"
        onClick={() =>
          open({ kind: 'page', pageId: item.kind === 'task' ? item.task.pageId : item.memo.id })
        }
        className={cn(
          'min-w-0 flex-1 truncate text-left text-sm hover:underline',
          item.kind === 'task' && item.task.status === 'done' && 'text-fg-muted line-through',
        )}
      >
        {item.kind === 'task'
          ? displayTitle(item.task.title) || t('page.untitled')
          : item.memo.title}
      </button>
    </li>
  );

  return (
    <div className="flex flex-col">
      {overdue.length > 0 && (
        <button
          type="button"
          onClick={() => open({ kind: 'tasks', tab: 'overdue' })}
          className="mb-2 flex items-center gap-2 self-start rounded-full bg-accent-soft px-3 py-1 text-xs font-medium text-accent-ink"
        >
          {t('dashboard.day.overdue', { count: overdue.length })}
          <ArrowRight className="size-3" />
        </button>
      )}
      {items.length === 0 ? (
        <p className="py-3 text-sm text-fg-muted">{t('dashboard.day.empty')}</p>
      ) : (
        <ul className="relative before:absolute before:top-2 before:bottom-2 before:left-[6px] before:w-px before:bg-border">
          {items.slice(0, MAX).map(row)}
        </ul>
      )}
      <button
        type="button"
        onClick={() => open({ kind: 'tasks', tab: 'today' })}
        className="mt-2 flex items-center gap-1 self-start rounded px-1 text-xs text-fg-muted hover:bg-bg-hover hover:text-fg"
      >
        {items.length > MAX
          ? t('dashboard.moreTasks', { count: items.length - MAX })
          : t('dashboard.allTasks')}
        <ArrowRight className="size-3" />
      </button>
    </div>
  );
}

/** Today's progress as a ring, and what comes next. */
export function ProgressBlock() {
  const { t } = useTranslation();
  const tasks = useTasks();
  const memos = useMemos();
  const [open, setOpen] = useState(false);
  if (!tasks || !memos) return null;
  const today = todayIso();
  const doneList = tasks.filter((x) => x.status === 'done' && x.doneAt === today);
  const leftList = tasks.filter((x) => isOpen(x.status) && x.due && x.due <= today);
  const done = doneList.length;
  const left = leftList.length;
  const total = done + left;
  const share = total ? done / total : 0;
  const now = new Date();
  const clock = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  const next = dayItems(tasks, memos, today).items.find(
    (i) => i.time && i.time >= clock && (i.kind === 'memo' || isOpen(i.task.status)),
  );
  const r = 15.5;
  const length = 2 * Math.PI * r;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('dashboard.progress.show')}
        className="-m-2 flex items-center gap-4 rounded-xl p-2 text-left hover:bg-bg-hover"
      >
        <svg viewBox="0 0 40 40" className="size-20 shrink-0 -rotate-90" aria-hidden>
          <circle cx="20" cy="20" r={r} fill="none" stroke="var(--bg-active)" strokeWidth="5" />
          {share > 0 && (
            <circle
              cx="20"
              cy="20"
              r={r}
              fill="none"
              stroke="var(--accent-bright)"
              strokeWidth="5"
              strokeLinecap="round"
              strokeDasharray={`${share * length} ${length}`}
            />
          )}
        </svg>
        <div className="min-w-0">
          <p
            className="text-lg font-semibold tracking-tight"
            role="status"
            aria-label={t('dashboard.progress.label', { done, total })}
          >
            {total ? t('dashboard.progress.count', { done, total }) : t('dashboard.progress.none')}
          </p>
          {next && (
            <p className="truncate text-sm text-fg-muted">
              {t('dashboard.progress.next', {
                title: next.kind === 'task' ? displayTitle(next.task.title) : next.memo.title,
                time: next.time,
              })}
            </p>
          )}
        </div>
      </button>
      <Dialog open={open} onOpenChange={setOpen} title={t('dashboard.progress.title')}>
        <TaskList
          title={t('dashboard.progress.doneToday', { count: done })}
          tasks={doneList}
          empty={t('dashboard.progress.noneDone')}
        />
        <TaskList
          title={t('dashboard.progress.stillOpen', { count: left })}
          tasks={leftList}
          empty={t('dashboard.progress.allDone')}
        />
      </Dialog>
    </>
  );
}

function TaskList({ title, tasks, empty }: { title: string; tasks: Task[]; empty: string }) {
  return (
    <section className="mb-4 last:mb-0">
      <h3 className="mb-1 text-xs font-medium tracking-wide text-fg-muted uppercase">{title}</h3>
      {tasks.length === 0 ? (
        <p className="py-2 text-sm text-fg-muted">{empty}</p>
      ) : (
        tasks.map((x) => <TaskRow key={x.id} task={x} projectTitle={null} compact />)
      )}
    </section>
  );
}
